import { Suspense, useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExternalLink, Mail, Phone, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { BuyerLeadDrawerDetails } from '@modules/_shared/components/buyer-lead/BuyerLeadEditDrawer';
import { CopyableText } from '@modules/_shared/components/CopyableText';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { LotSelect } from '@modules/_shared/components/LotsSelect';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { TransactionCard } from '@modules/_shared/components/TransactionCard';
import {
  CONTENT_HEIGHT,
  TwoPaneLayout,
} from '@modules/_shared/components/TwoPaneLayout';
import { formatPhone } from '@modules/_shared/utils/formatting';
import { EnquiryCreateBuyerLead } from '@modules/enquiries/components/EnquiryCreateBuyerLead';
import { useLocale } from '@shared/hooks/locale';

const GET_ENQUIRY_BUYER_LEAD = gql(/* GraphQL */ `
  query GetEnquiryBuyerLead($id: uuid!) {
    enquiries_by_pk(id: $id) {
      id
      created_at
      message
      buyer_lead_id
      lot_id
      property_transaction {
        id
        ...TransactionCard
      }
      portal {
        name
      }
      ...EnquiryCreateBuyerLead
    }
  }
`);

const ASSIGN_ENQUIRY_LISTING = gql(/* GraphQL */ `
  mutation AssignEnquiryListing($enquiryId: uuid!, $lotId: uuid!) {
    update_enquiries_by_pk(
      pk_columns: { id: $enquiryId }
      _set: { lot_id: $lotId }
    ) {
      id
      buyer_lead_id
      lot_id
      ...EnquiryCreateBuyerLead
    }
  }
`);

const CARD_GAP = 2;
const CARD_CONTENT_GAP = 1.5;
const cardSx = { m: '0 !important' } as const;

const InfoRow: React.FC<{
  icon: React.ReactNode;
  text: string;
  copyable?: boolean;
}> = ({ icon, text, copyable = false }) => {
  const content = (
    <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box sx={{ color: 'text.secondary', flexShrink: 0, display: 'flex' }}>
        {icon}
      </Box>
      {copyable ? <CopyableText text={text}>{content}</CopyableText> : content}
    </Box>
  );
};

const InfoRowSkeleton = ({ width }: { width: number }) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
    <Skeleton variant="circular" width={16} height={16} />
    <Skeleton variant="text" width={width} />
  </Stack>
);

const BuyerLeadSetupLoadingState: React.FC<{
  alertText: string;
  buttonLabel: string;
}> = ({ alertText, buttonLabel }) => (
  <Stack sx={{ gap: CARD_CONTENT_GAP }}>
    <Alert severity="warning">{alertText}</Alert>
    <Button loading variant="contained" sx={{ alignSelf: 'flex-start' }}>
      {buttonLabel}
    </Button>
  </Stack>
);

const EnquiryBuyerLeadDrawerSkeleton: React.FC<{
  isMobile: boolean;
  enquiryTitle: string;
  listingTitle: string;
  buyerLeadTitle: string;
}> = ({ isMobile, enquiryTitle, listingTitle, buyerLeadTitle }) => {
  const enquiryCards = (
    <Stack sx={{ gap: CARD_GAP }}>
      <ListingAccordion defaultExpanded disableCollapse sx={cardSx}>
        <Stack aria-hidden sx={{ gap: CARD_CONTENT_GAP }}>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Skeleton variant="text" width={150} height={28} />
            <Skeleton variant="text" width={64} />
          </Stack>
          <InfoRowSkeleton width={120} />
          <InfoRowSkeleton width={180} />
          <InfoRowSkeleton width={100} />
        </Stack>
      </ListingAccordion>

      <ListingAccordion
        title={enquiryTitle}
        defaultExpanded
        disableCollapse
        sx={cardSx}
      >
        <Skeleton aria-hidden variant="text" width="72%" />
      </ListingAccordion>

      <ListingAccordion
        title={listingTitle}
        defaultExpanded
        disableCollapse
        sx={cardSx}
      >
        <Stack aria-hidden sx={{ gap: CARD_CONTENT_GAP }}>
          <Skeleton variant="text" width="78%" />
          <Skeleton variant="rounded" height={40} />
        </Stack>
      </ListingAccordion>
    </Stack>
  );

  const buyerLeadCard = (
    <ListingAccordion
      title={buyerLeadTitle}
      defaultExpanded
      disableCollapse
      sx={cardSx}
    >
      <Skeleton aria-hidden variant="text" width="58%" />
    </ListingAccordion>
  );

  return isMobile ? (
    <Box sx={{ overflowY: 'auto', height: CONTENT_HEIGHT, p: 2 }}>
      <Stack sx={{ gap: CARD_GAP }}>
        {enquiryCards}
        {buyerLeadCard}
      </Stack>
    </Box>
  ) : (
    <TwoPaneLayout leftPane={enquiryCards} rightPane={buyerLeadCard} />
  );
};

export const EnquiryBuyerLeadDrawerContent: React.FC<{
  enquiryId: string;
  initialBuyerLeadId?: string | null;
}> = ({ enquiryId, initialBuyerLeadId }) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const { data, error, loading, refetch } = useQuery(GET_ENQUIRY_BUYER_LEAD, {
    variables: { id: enquiryId },
  });

  const [assignListing, { loading: assignListingLoading }] = useMutation(
    ASSIGN_ENQUIRY_LISTING,
    { onError: setErrorLike },
  );

  const enquiry = data?.enquiries_by_pk;
  const buyerLeadId = enquiry?.buyer_lead_id ?? initialBuyerLeadId ?? null;

  const refreshEnquiry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleListingChanged = useCallback(
    (lotId: string | null) => {
      if (lotId == null) {
        return;
      }

      assignListing({
        variables: {
          enquiryId,
          lotId,
        },
      }).then(result => {
        if (result.error == null) {
          refreshEnquiry();
        }
      });
    },
    [assignListing, enquiryId, refreshEnquiry],
  );

  if (buyerLeadId != null) {
    return (
      <BuyerLeadDrawerDetails buyerLeadId={buyerLeadId} enquiryId={enquiryId} />
    );
  }

  if (loading) {
    return (
      <EnquiryBuyerLeadDrawerSkeleton
        isMobile={isMobile}
        enquiryTitle={t('Enquiry')}
        listingTitle={t('Listing')}
        buyerLeadTitle={t('Create buyer lead')}
      />
    );
  }

  if (error || enquiry == null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error ? (
          <pre>{JSON.stringify(error, null, 2)}</pre>
        ) : (
          t('Enquiry not found')
        )}
      </Alert>
    );
  }

  const contactName =
    `${enquiry.first_name ?? ''} ${enquiry.last_name ?? ''}`.trim() ||
    t('Unknown');
  const contactEmail =
    enquiry.email_address ?? enquiry.raw_email_address ?? null;
  const contactPhone =
    formatPhone(enquiry.phone_number ?? enquiry.raw_phone_number) ?? null;
  const hasListing = enquiry.lot_id != null;
  const portalName = enquiry.portal?.name;
  const enquiryTitle =
    portalName != null ? `${t('Enquiry')} (${portalName})` : t('Enquiry');

  const enquiryCards = (
    <Stack sx={{ gap: CARD_GAP }}>
      <ListingAccordion defaultExpanded disableCollapse sx={cardSx}>
        <Stack sx={{ gap: CARD_CONTENT_GAP }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {contactName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                pt: 0.5,
              }}
            >
              <TimeAgo dateString={enquiry.created_at} addSuffix />
            </Typography>
          </Box>

          {contactPhone != null && (
            <InfoRow icon={<Phone size={16} />} text={contactPhone} copyable />
          )}
          {contactEmail != null && (
            <InfoRow icon={<Mail size={16} />} text={contactEmail} copyable />
          )}
          {portalName != null && (
            <InfoRow icon={<Tag size={16} />} text={portalName} />
          )}
        </Stack>
      </ListingAccordion>

      <ListingAccordion
        title={enquiryTitle}
        defaultExpanded
        disableCollapse
        sx={cardSx}
      >
        <Typography
          variant="body2"
          sx={{
            color: enquiry.message ? 'text.primary' : 'text.secondary',
            whiteSpace: 'pre-wrap',
          }}
        >
          {enquiry.message || '-'}
        </Typography>
      </ListingAccordion>

      <ListingAccordion
        title={t('Listing')}
        defaultExpanded
        disableCollapse
        sx={cardSx}
      >
        <Stack sx={{ gap: CARD_CONTENT_GAP }}>
          {!hasListing && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('No listing. You can attach one using the input below:')}
            </Typography>
          )}
          <FormControl fullWidth>
            <LotSelect
              lotId={enquiry.lot_id ?? undefined}
              onChange={handleListingChanged}
              loading={assignListingLoading}
              InputProps={{
                placeholder: t('Assign a listing'),
              }}
            />
          </FormControl>
          {hasListing && (
            <Button
              variant="outlined"
              startIcon={<ExternalLink size={16} />}
              component={Link}
              to={`/listings/${enquiry.lot_id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('View listing')}
            </Button>
          )}
        </Stack>
      </ListingAccordion>
      {enquiry.property_transaction != null && (
        <ListingAccordion
          title={t('Transactions')}
          defaultExpanded
          disableCollapse
          sx={cardSx}
        >
          <Link
            to={`/transactions/${enquiry.property_transaction.id}`}
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <TransactionCard transaction={enquiry.property_transaction} />
          </Link>
        </ListingAccordion>
      )}
    </Stack>
  );

  const buyerLeadCard = (
    <ListingAccordion
      title={t('Create buyer lead')}
      defaultExpanded
      disableCollapse
      sx={cardSx}
    >
      {hasListing ? (
        <Suspense
          fallback={
            <BuyerLeadSetupLoadingState
              alertText={t(
                'No buyer lead created for this enquiry. We found matching buyer lead(s) for the listing. Attach one of them:',
              )}
              buttonLabel={t('Create buyer lead')}
            />
          }
        >
          <EnquiryCreateBuyerLead
            enquiry={enquiry}
            onUserSelected={() => undefined}
            onBuyerLeadChanged={refreshEnquiry}
          />
        </Suspense>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Choose a listing to continue.')}
        </Typography>
      )}
    </ListingAccordion>
  );

  return (
    <>
      {isMobile ? (
        <Box sx={{ overflowY: 'auto', height: CONTENT_HEIGHT, p: 2 }}>
          <Stack sx={{ gap: CARD_GAP }}>
            {enquiryCards}
            {buyerLeadCard}
          </Stack>
        </Box>
      ) : (
        <TwoPaneLayout leftPane={enquiryCards} rightPane={buyerLeadCard} />
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
